import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { ShoppingCart, Loader2, X, AlertCircle, Search, Eye } from "lucide-react";
import {
  getAdminOrders, getAdminOrder, updateOrderStatus, updatePaymentStatus,
  AdminOrder, OrderStatus, PaymentStatus,
} from "../../api/services/adminOrderService";
import { getErrorMessage } from "@/utils/errorUtils";

const ORDER_STATES: OrderStatus[] = ["pending", "processing", "completed", "cancelled"];
const PAY_STATES: PaymentStatus[] = ["pending", "paid", "failed", "refunded"];
const fld = "rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

const statusBadge = (s?: string) => ({
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  processing: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  paid: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  refunded: "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400",
} as Record<string, string>)[s || "pending"] || "bg-gray-100 text-gray-700";

const userName = (u: AdminOrder["userId"]) => {
  if (!u || typeof u === "string") return "—";
  return [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || "—";
};

export default function AdminOrders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
  const [payFilter, setPayFilter] = useState<PaymentStatus | "">("");
  const [detail, setDetail] = useState<AdminOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { orders } = await getAdminOrders({
        status: statusFilter || undefined,
        paymentStatus: payFilter || undefined,
        search: search || undefined,
        limit: 100,
      });
      setOrders(orders);
    } catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, payFilter]);

  const openDetail = async (id?: string) => {
    if (!id) return;
    setDetailLoading(true); setDetail(null);
    try { setDetail(await getAdminOrder(id)); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to load order"); }
    finally { setDetailLoading(false); }
  };

  const changeStatus = async (s: OrderStatus) => {
    if (!detail?._id) return;
    setBusy(true);
    try {
      const updated = await updateOrderStatus(detail._id, s);
      if (updated) { setDetail(updated); await load(); }
    } catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to update status"); }
    finally { setBusy(false); }
  };

  const changePayment = async (s: PaymentStatus) => {
    if (!detail?._id) return;
    setBusy(true);
    try {
      const updated = await updatePaymentStatus(detail._id, s);
      if (updated) { setDetail(updated); await load(); }
    } catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to update payment status"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <ShoppingCart className="w-6 h-6 text-blue-600" />
        <h1 className="text-xl sm:text-2xl font-bold">{t('admin.orders.title', 'Orders')}</h1>
        <span className="text-sm text-gray-500">({orders.length})</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className={fld + " w-full pl-9"} placeholder={t('admin.orders.search', 'Search order #…')} value={search}
                 onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select className={fld} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as never)}>
          <option value="">{t('admin.orders.filter.allStatuses', 'All statuses')}</option>
          {ORDER_STATES.map(s => <option key={s} value={s}>{t(`admin.orders.status.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}</option>)}
        </select>
        <select className={fld} value={payFilter} onChange={(e) => setPayFilter(e.target.value as never)}>
          <option value="">{t('admin.orders.filter.allPayments', 'All payments')}</option>
          {PAY_STATES.map(s => <option key={s} value={s}>{t(`admin.orders.paymentStatus.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}</option>)}
        </select>
      </div>

      {err && !detail && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-gray-500">{t('admin.orders.noOrders', 'No orders found.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.order', 'Order #')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.customer', 'Customer')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.total', 'Total')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.payment', 'Payment')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.status', 'Status')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.orders.table.date', 'Date')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('admin.orders.table.view', 'View')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {orders.map((o) => (
                  <tr key={o._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="px-4 py-3 font-mono font-medium">{o.orderNumber}</td>
                    <td className="px-4 py-3">{userName(o.userId)}</td>
                    <td className="px-4 py-3 font-medium">{o.currency || "USD"} {Number(o.totalAmount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={"px-2 py-1 rounded-full text-xs font-medium " + statusBadge(o.paymentStatus)}>{t(`admin.orders.paymentStatus.${o.paymentStatus}`, (o.paymentStatus || "").charAt(0).toUpperCase() + (o.paymentStatus || "").slice(1))}</span></td>
                    <td className="px-4 py-3"><span className={"px-2 py-1 rounded-full text-xs font-medium " + statusBadge(o.status)}>{t(`admin.orders.status.${o.status}`, (o.status || "").charAt(0).toUpperCase() + (o.status || "").slice(1))}</span></td>
                    <td className="px-4 py-3 text-gray-500">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openDetail(o._id)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><Eye className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
              <h2 className="text-lg font-semibold">{t('admin.orders.modal.title', 'Order {{number}}', { number: detail?.orderNumber || "" })}</h2>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
            </div>
            {detailLoading || !detail ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
            ) : (
              <div className="p-5 space-y-5">
                {err && <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-3 py-2 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-gray-500">{t('admin.orders.modal.customer', 'Customer')}</div><div className="font-medium">{userName(detail.userId)}</div></div>
                  <div><div className="text-gray-500">{t('admin.orders.modal.placed', 'Placed')}</div><div className="font-medium">{detail.createdAt ? new Date(detail.createdAt).toLocaleString() : "—"}</div></div>
                  <div><div className="text-gray-500">{t('admin.orders.modal.paymentMethod', 'Payment method')}</div><div className="font-medium capitalize">{detail.paymentMethod || "—"}</div></div>
                  <div><div className="text-gray-500">{t('admin.orders.modal.paymentRef', 'Payment ref')}</div><div className="font-mono text-xs break-all">{detail.paymentRef || "—"}</div></div>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">{t('admin.orders.modal.items', 'Items')}</div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                    {(detail.items || []).map((it, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <div className="w-9 h-9 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                          {it.thumbnailUrl ? <img src={it.thumbnailUrl} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1"><div className="font-medium">{it.name}</div><div className="text-xs text-gray-500">{t('admin.orders.modal.qty', 'Qty')} {it.quantity} × {Number(it.unitPrice).toFixed(2)}</div></div>
                        <div className="font-medium">{Number(it.lineTotal).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-6 mt-3 text-sm">
                    <div className="text-gray-500">{t('admin.orders.modal.subtotal', 'Subtotal')} <span className="text-gray-900 dark:text-white font-medium ml-2">{Number(detail.subtotal || 0).toFixed(2)}</span></div>
                    {detail.platformFee ? <div className="text-gray-500">{t('admin.orders.modal.fee', 'Fee')} <span className="text-gray-900 dark:text-white font-medium ml-2">{Number(detail.platformFee).toFixed(2)}</span></div> : null}
                    <div className="text-gray-500">{t('admin.orders.modal.totalAmount', 'Total')} <span className="text-gray-900 dark:text-white font-bold ml-2">{detail.currency || "USD"} {Number(detail.totalAmount || 0).toFixed(2)}</span></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.orders.form.orderStatus', 'Order status')}</label>
                    <select className={fld + " w-full"} value={detail.status} disabled={busy} onChange={(e) => changeStatus(e.target.value as OrderStatus)}>
                      {ORDER_STATES.map(s => <option key={s} value={s}>{t(`admin.orders.status.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.orders.form.paymentStatus', 'Payment status')}</label>
                    <select className={fld + " w-full"} value={detail.paymentStatus} disabled={busy} onChange={(e) => changePayment(e.target.value as PaymentStatus)}>
                      {PAY_STATES.map(s => <option key={s} value={s}>{t(`admin.orders.paymentStatus.${s}`, s.charAt(0).toUpperCase() + s.slice(1))}</option>)}
                    </select>
                  </div>
                </div>

                {detail.activationTimeline && detail.activationTimeline.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-2">{t('admin.orders.modal.timeline', 'Timeline')}</div>
                    <div className="space-y-1.5">
                      {detail.activationTimeline.map((ev, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          <span className="text-gray-700 dark:text-gray-300">{ev.label}</span>
                          <span className="ml-auto">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
